import {
  BrandingCard,
  ClientEventsCard,
  ClosingPageCard,
  ConversationBehaviorCard,
  ConversationReportsCard,
  DistributionCard,
  DocumentsGenerationCard,
  EscalationWebhookCard,
  ExperimentalCard,
  ExternalToolsCard,
  ExtraSettingsCard,
  GenieInsights,
  GenieMailboxCard,
  GenieSuiteCard,
  GeniePageCard,
  IdentityProfileCard,
  InfoLinksCard,
  KnowledgeCard,
  ModelIntelligenceCard,
  PrivateContextSourceCard,
  PronunciationsCard,
  SystemPromptCard,
  UsageBillingCard,
  VoiceSpeechCard,
  WelcomeMessagesCard,
} from "@/components/agent/details";
import { GenieDetailSectionsProps } from "./genieDetailSections";

// Grid layout pairs cards inside one anchored wrapper, so a card-level
// `?section=` falls back to the wrapper that holds it.
export const VOICE_SECTION_FALLBACKS: Record<string, string> = {
  "section-voice": "section-identity",
  "section-welcome": "section-prompts",
  "section-pronunciations": "section-prompts",
  "section-context-source": "section-resources",
  "section-closing": "section-resources",
  "section-webhook": "section-reports",
  "section-client-events": "section-tools",
  "section-behavior": "section-intelligence",
  "section-usage": "section-extra",
};

export const VoiceGenieSections = ({
  genie,
  brand,
  layout,
  isAdmin,
  knowledgeNotice,
  onOpenModal,
  distributionRef,
}: GenieDetailSectionsProps) => {
  if (layout === "grid") {
    return (
      <div className="flex flex-col gap-6">
        <div id="section-distribution" ref={distributionRef}>
          <DistributionCard agent={genie} brand={brand} onAction={onOpenModal} />
        </div>
        <div id="section-suite">
          <GenieSuiteCard agent={genie} brand={brand} />
        </div>
        <div id="section-identity" className="grid grid-cols-2 gap-6">
          <IdentityProfileCard agent={genie} brand={brand} />
          <VoiceSpeechCard agent={genie} brand={brand} isAdmin={isAdmin} />
        </div>
        <div id="section-prompts" className="grid grid-cols-2 gap-6">
          <SystemPromptCard agent={genie} brand={brand} />
          <div className="space-y-6">
            <WelcomeMessagesCard agent={genie} brand={brand} />
            <PronunciationsCard agent={genie} brand={brand} />
          </div>
        </div>
        <div id="section-insights">
          <GenieInsights agent={genie} />
        </div>
        <div id="section-knowledge">
          {knowledgeNotice}
          <KnowledgeCard agent={genie} brand={brand} />
        </div>
        <div id="section-documents">
          <DocumentsGenerationCard agent={genie} brand={brand} />
        </div>
        <div id="section-resources" className="grid grid-cols-2 gap-6">
          <PrivateContextSourceCard agent={genie} brand={brand} />
          <InfoLinksCard agent={genie} brand={brand} />
          <ClosingPageCard agent={genie} brand={brand} />
        </div>
        <div id="section-genie-page">
          <GeniePageCard agent={genie} brand={brand} />
        </div>
        <div id="section-branding">
          <BrandingCard agent={genie} brand={brand} onSwitchModal={onOpenModal} />
        </div>
        <div id="section-mailbox">
          <GenieMailboxCard agent={genie} brand={brand} />
        </div>
        <div id="section-reports" className="grid grid-cols-2 gap-6">
          <ConversationReportsCard agent={genie} brand={brand} />
          <EscalationWebhookCard agent={genie} brand={brand} />
        </div>
        <div id="section-tools" className="grid grid-cols-2 gap-6">
          <ExternalToolsCard agent={genie} brand={brand} />
          <ClientEventsCard agent={genie} brand={brand} />
        </div>
        <div id="section-intelligence" className="grid grid-cols-2 gap-6">
          <ModelIntelligenceCard agent={genie} brand={brand} />
          <ConversationBehaviorCard agent={genie} brand={brand} />
        </div>
        <div id="section-extra" className="grid grid-cols-2 gap-6">
          <ExtraSettingsCard agent={genie} brand={brand} />
          <UsageBillingCard agent={genie} brand={brand} />
        </div>
        {isAdmin && (
          <div id="section-experimental">
            <ExperimentalCard agent={genie} brand={brand} />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4 lg:space-y-6">
      <div id="section-distribution" ref={distributionRef}>
        <DistributionCard agent={genie} brand={brand} onAction={onOpenModal} />
      </div>
      <div id="section-suite">
        <GenieSuiteCard agent={genie} brand={brand} />
      </div>
      <div id="section-identity">
        <IdentityProfileCard agent={genie} brand={brand} />
      </div>
      <div id="section-voice">
        <VoiceSpeechCard agent={genie} brand={brand} isAdmin={isAdmin} />
      </div>
      <div id="section-prompts">
        <SystemPromptCard agent={genie} brand={brand} />
      </div>
      <div id="section-welcome">
        <WelcomeMessagesCard agent={genie} brand={brand} />
      </div>
      <div id="section-pronunciations">
        <PronunciationsCard agent={genie} brand={brand} />
      </div>
      <div id="section-insights">
        <GenieInsights agent={genie} />
      </div>
      <div id="section-knowledge">
        {knowledgeNotice}
        <KnowledgeCard agent={genie} brand={brand} />
      </div>
      <div id="section-documents">
        <DocumentsGenerationCard agent={genie} brand={brand} />
      </div>
      <div id="section-context-source">
        <PrivateContextSourceCard agent={genie} brand={brand} />
      </div>
      <div id="section-resources">
        <InfoLinksCard agent={genie} brand={brand} />
      </div>
      <div id="section-closing">
        <ClosingPageCard agent={genie} brand={brand} />
      </div>
      <div id="section-branding">
        <BrandingCard agent={genie} brand={brand} onSwitchModal={onOpenModal} />
      </div>
      <div id="section-mailbox">
        <GenieMailboxCard agent={genie} brand={brand} />
      </div>
      <div id="section-reports">
        <ConversationReportsCard agent={genie} brand={brand} />
      </div>
      <div id="section-webhook">
        <EscalationWebhookCard agent={genie} brand={brand} />
      </div>
      <div id="section-tools">
        <ExternalToolsCard agent={genie} brand={brand} />
      </div>
      <div id="section-client-events">
        <ClientEventsCard agent={genie} brand={brand} />
      </div>
      <div id="section-intelligence">
        <ModelIntelligenceCard agent={genie} brand={brand} />
      </div>
      <div id="section-behavior">
        <ConversationBehaviorCard agent={genie} brand={brand} />
      </div>
      <div id="section-extra">
        <ExtraSettingsCard agent={genie} brand={brand} />
      </div>
      <div id="section-usage">
        <UsageBillingCard agent={genie} brand={brand} />
      </div>
      {isAdmin && (
        <div id="section-experimental">
          <ExperimentalCard agent={genie} brand={brand} />
        </div>
      )}
    </div>
  );
};
